import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Monitor,
  Shield,
  Download,
  RefreshCw,
  Search,
  AlertTriangle,
  Activity,
  Cpu,
  HardDrive,
  Clock,
  MoreVertical,
  Trash2,
  Settings,
  Play,
  Wifi,
  WifiOff,
  Apple,
  Globe,
  Laptop,
  ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function DeviceManagement() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    threats_blocked: 0,
    dns_blocked: 0
  });
  const [threatTrends, setThreatTrends] = useState([]);
  const [versionDistribution, setVersionDistribution] = useState([]);
  const [healthMetrics, setHealthMetrics] = useState([]);
  
  useEffect(() => {
    fetchDevices();
    fetchStats();
    fetchThreatTrends();
    fetchVersionDistribution();
    fetchHealthMetrics();
  }, []);

  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data.agents || []);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      setDevices(getDemoDevices());
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/agents/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      setStats({
        total: 12,
        online: 10,
        offline: 2,
        threats_blocked: 1247,
        dns_blocked: 8934
      });
    }
  };

  const fetchThreatTrends = async () => {
    const days = 7;
    const data = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        threats: Math.floor(Math.random() * 50) + 10,
        blocked: Math.floor(Math.random() * 200) + 100
      });
    }
    setThreatTrends(data);
  };

  const fetchVersionDistribution = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/agents/updates/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersionDistribution(response.data.version_distribution || []);
    } catch (error) {
      setVersionDistribution([
        { version: '1.0.0', count: 8 },
        { version: '0.9.5', count: 3 },
        { version: '0.9.0', count: 1 }
      ]);
    }
  };

  const fetchHealthMetrics = async () => {
    const hours = 24;
    const data = [];
    for (let i = hours - 1; i >= 0; i--) {
      const hour = new Date();
      hour.setHours(hour.getHours() - i);
      data.push({
        time: hour.toLocaleTimeString('en-US', { hour: '2-digit' }),
        cpu: Math.floor(Math.random() * 30) + 10,
        memory: Math.floor(Math.random() * 20) + 30
      });
    }
    setHealthMetrics(data);
  };

  const handleDeviceAction = async (deviceId, action) => {
    try {
      const token = localStorage.getItem('token');
      if (action === 'unenroll') {
        await axios.delete(`${API}/api/agents/${deviceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Device removed successfully');
      } else {
        await axios.post(`${API}/api/agents/${deviceId}/command`, 
          { command: action },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success(`${action} command sent`);
      }
      fetchDevices();
    } catch (error) {
      toast.error(`Failed to ${action} device`);
    }
  };

  const getDemoDevices = () => [
    {
      id: '1',
      device_id: 'dev_001',
      device_name: 'Ryan-MacBookPro',
      hostname: 'Johns-MacBook-Pro',
      os_type: 'macos',
      os_version: '14.2.1',
      agent_version: '1.0.0',
      status: 'online',
      last_seen: new Date().toISOString(),
      threats_detected: 3,
      dns_blocked: 127,
      cpu_usage: 12,
      memory_usage: 45,
      disk_free_gb: 120
    },
    {
      id: '2',
      device_id: 'dev_002',
      device_name: 'Office-iMac',
      hostname: 'Sarahs-iMac',
      os_type: 'macos',
      os_version: '14.1',
      agent_version: '1.0.0',
      status: 'online',
      last_seen: new Date().toISOString(),
      threats_detected: 1,
      dns_blocked: 89,
      cpu_usage: 8,
      memory_usage: 38,
      disk_free_gb: 250
    },
    {
      id: '3',
      device_id: 'dev_003',
      device_name: 'Dev-MacMini',
      hostname: 'DevMac-Mini',
      os_type: 'macos',
      os_version: '13.6',
      agent_version: '0.9.5',
      status: 'offline',
      last_seen: new Date(Date.now() - 3600000).toISOString(),
      threats_detected: 0,
      dns_blocked: 45,
      cpu_usage: 0,
      memory_usage: 0,
      disk_free_gb: 80
    }
  ];

  const filteredDevices = devices.filter(device => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      device.hostname?.toLowerCase().includes(searchLower) ||
      device.device_name?.toLowerCase().includes(searchLower) ||
      device.device_id?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  const getOsIcon = (osType) => {
    if (osType === 'macos') return Apple;
    return Laptop;
  };

  return (
    <div className="space-y-6" data-testid="device-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Device Management</h1>
          <p className="text-slate-400 text-sm mt-1">Monitor and manage your protected devices</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDevices} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/agent')} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4" />
            Get Agent
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total Devices</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Monitor className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Online</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{stats.online}</p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Wifi className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Offline</p>
                <p className="text-2xl font-bold text-slate-400 mt-1">{stats.offline}</p>
              </div>
              <div className="p-3 bg-slate-500/10 rounded-lg">
                <WifiOff className="w-5 h-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Threats Blocked</p>
                <p className="text-2xl font-bold text-red-400 mt-1">{stats.threats_blocked?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">DNS Blocked</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">{stats.dns_blocked?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Threat Trends Chart */}
        <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base">Protection Activity (7 Days)</CardTitle>
            <CardDescription>Threats detected and blocked across all devices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={threatTrends}>
                  <defs>
                    <linearGradient id="threatGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="blockedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Area type="monotone" dataKey="threats" stroke="#ef4444" fill="url(#threatGradient)" strokeWidth={2} name="Threats" />
                  <Area type="monotone" dataKey="blocked" stroke="#3b82f6" fill="url(#blockedGradient)" strokeWidth={2} name="Blocked" />
                  <Legend />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Version Distribution */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base">Agent Versions</CardTitle>
            <CardDescription>Distribution of installed versions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={versionDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="version"
                    label={({ version, percent }) => `${version} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {versionDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    formatter={(value, name) => [`${value} devices`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base">Fleet Health (24 Hours)</CardTitle>
          <CardDescription>Average CPU and memory usage across all devices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={healthMetrics}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="time" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={12} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value) => [`${value}%`]}
                />
                <Line type="monotone" dataKey="cpu" stroke="#3b82f6" strokeWidth={2} dot={false} name="CPU" />
                <Line type="monotone" dataKey="memory" stroke="#10b981" strokeWidth={2} dot={false} name="Memory" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Device List */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-white">Protected Devices</CardTitle>
              <CardDescription>All enrolled endpoint devices</CardDescription>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search devices..."
                  className="pl-10 bg-slate-800 border-slate-700 w-full sm:w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="text-center py-12">
              <Monitor className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No devices yet</h3>
              <p className="text-slate-400 mb-4">Download the agent and enroll your first device</p>
              <Button onClick={() => navigate('/agent')} className="gap-2">
                <Download className="w-4 h-4" />
                Get Agent
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDevices.map((device) => {
                const OsIcon = getOsIcon(device.os_type);
                // Extract system info - handle both flat and nested structures
                const systemInfo = device.system_info || {};
                const cpuUsage = device.cpu_usage ?? systemInfo.cpu_usage ?? 0;
                const memoryUsage = device.memory_usage ?? systemInfo.memory_usage ?? 0;
                const diskFreeGb = device.disk_free_gb ?? systemInfo.disk_free_gb ?? 0;
                // Extract threat stats
                const threatStats = device.threat_stats || {};
                const threatsDetected = device.threats_detected ?? threatStats.total_threats ?? 0;
                const dnsBlocked = device.dns_blocked ?? threatStats.dns_blocked ?? 0;
                
                return (
                  <div
                    key={device.id || device.device_id}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors gap-4"
                    data-testid={`device-${device.device_id}`}
                  >
                    <div className="flex items-start gap-4 w-full sm:w-auto">
                      <div className={`p-2 rounded-lg ${device.status === 'online' || device.status === 'active' ? 'bg-green-500/10' : 'bg-slate-700/50'}`}>
                        <OsIcon className={`w-5 h-5 ${device.status === 'online' || device.status === 'active' ? 'text-green-400' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-white truncate">
                            {device.device_name || device.hostname}
                          </h3>
                          <Badge variant={device.status === 'online' || device.status === 'active' ? 'default' : 'secondary'} className={`text-xs ${device.status === 'online' || device.status === 'active' ? 'bg-green-500/20 text-green-400' : ''}`}>
                            {device.status === 'active' ? 'online' : device.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400 mt-1">
                          {device.device_name && device.hostname !== device.device_name && (
                            <span>{device.hostname}</span>
                          )}
                          <span>{device.os_type === 'macos' ? 'macOS' : device.os_type} {device.os_version}</span>
                          <span>v{device.agent_version}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatLastSeen(device.last_seen)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6 w-full sm:w-auto justify-between sm:justify-end">
                      {/* Health Indicators */}
                      <div className="flex gap-4 text-xs">
                        <div className="text-center">
                          <div className="flex items-center gap-1 text-blue-400">
                            <Cpu className="w-3 h-3" />
                            <span>{Math.round(cpuUsage)}%</span>
                          </div>
                          <span className="text-slate-500">CPU</span>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1 text-green-400">
                            <Activity className="w-3 h-3" />
                            <span>{Math.round(memoryUsage)}%</span>
                          </div>
                          <span className="text-slate-500">RAM</span>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1 text-amber-400">
                            <HardDrive className="w-3 h-3" />
                            <span>{Math.round(diskFreeGb)}GB</span>
                          </div>
                          <span className="text-slate-500">Free</span>
                        </div>
                      </div>
                      
                      {/* Threat Stats */}
                      <div className="flex gap-4 text-xs">
                        <div className="text-center">
                          <div className="text-red-400 font-medium">{threatsDetected}</div>
                          <span className="text-slate-500">Threats</span>
                        </div>
                        <div className="text-center">
                          <div className="text-blue-400 font-medium">{dnsBlocked}</div>
                          <span className="text-slate-500">Blocked</span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem onClick={() => handleDeviceAction(device.device_id, 'scan')} className="gap-2">
                            <Play className="w-4 h-4" />
                            Run Scan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeviceAction(device.device_id, 'update')} className="gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Check Updates
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeviceAction(device.device_id, 'sync_config')} className="gap-2">
                            <Settings className="w-4 h-4" />
                            Sync Config
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeviceAction(device.device_id, 'unenroll')} className="gap-2 text-red-400">
                            <Trash2 className="w-4 h-4" />
                            Remove Device
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
